import React, { useCallback } from "react";
import loginModalStore from "~/common/LoginModalV2/loginModalStore";
import { PlusModalData } from "~/common/PlusSubscriptionModal/Models";
import PlusSubscriptionModalStore, {
  ModalTypes,
} from "~/common/PlusSubscriptionModal/PlusSubscriptionModalStore";
import { User } from "~/common/Store/connectUserAndChat";

/**
 * Custom React hook for handling the logic to launch the Plus Subscription modal.
 *
 * This hook determines if the user is eligible to see the Plus modal based on their registration status
 * and the presence of valid addresses in the provided PlusModalData. It returns a handler that
 * either opens the login modal (if the user is not registered) or opens the appropriate Plus modal
 * (eligible or ineligible) based on the user's data.
 *
 * The returned handler accepts any React SyntheticEvent (mouse, keyboard, form, etc.), or can be called with no argument.
 * This allows you to use it as an event handler (e.g., for button clicks, keyboard events, or form submissions)
 * or to invoke it programmatically (such as on page load).
 *
 * @param {PlusModalData} [plusModalData] - Data containing valid addresses for Plus eligibility.
 * @param {User} [user] - The current user object.
 * @returns {{
 *   handlePlusModalLaunch: (event?: React.SyntheticEvent<Element, Event>) => void
 * }} An object containing a handler for launching the modal.
 *
 * @example
 * // Using with a button (e.g., mouse event)
 * const { handlePlusModalLaunch } = usePlusModalLauncher(plusModalData, user);
 * <button onClick={handlePlusModalLaunch}>Join Plus</button>
 * @example
 * // Calling directly (e.g., on page load)
 * useEffect(() => {
 *   if (shouldShowPlusModal) {
 *     handlePlusModalLaunch();
 *   }
 * }, [shouldShowPlusModal]);
 */
export default function usePlusModalLauncher(
  plusModalData?: PlusModalData,
  user?: User,
): {
  handlePlusModalLaunch: (event?: React.SyntheticEvent<Element, Event>) => void;
} {
  const hasValidAddresses = !!plusModalData?.validAddresses?.length;
  const { setModalOpen } = PlusSubscriptionModalStore.getState();

  const handlePlusModalLaunch = useCallback(
    (event?: React.SyntheticEvent<Element, Event>) => {
      event?.preventDefault();

      if (!user?.registered) {
        loginModalStore.openLogin({
          targetUrl: window.location.pathname,
          queryParams: "?showform=1",
        });
        return;
      }

      setModalOpen(hasValidAddresses ? ModalTypes.PLUS : ModalTypes.INELIGIBLE);
    },
    [user?.registered, hasValidAddresses, setModalOpen],
  );

  return {
    handlePlusModalLaunch,
  };
}
